import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import {
    AlertTypes,
    DataGridPro,
    Mui,
    MuiDataGrid,
    MuiIcons,
    setAlert,
} from '@leap4/frontend';
import { tableNames } from '../../../common/data/enums';
import { dateToStringFormat } from '../../utils/functions';
import { getSavedFilterModel, saveFilterModel } from '../../../utils/filterUtils';
import { IDistinctLineSpec, ILineSpecPublisher, IOldLineRow, ITrackedPublisher } from './appAdsLines.interface';
import {
    activateLineOnPublishers,
    errorMessage,
    removeLineFromPublishers,
    fetchDistinctLineSpecs,
    fetchOldLines,
    fetchPublishersWithCounts,
    purgeRemovedLines,
} from './appAdsLines.api';
import { ImportToAllModal } from './importToAllModal';
import { describeSaveResult, GenerateLinesModal } from './generateLinesModal';
import { searchRows } from './rowSearch';
import { useWrappingGrid } from './wrappingGrid';

type GridColDef = MuiDataGrid.GridColDef;

const {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    List,
    ListItem,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} = Mui;
const {
    PlaylistAdd: PlaylistAddIcon,
    PlaylistRemove: PlaylistRemoveIcon,
    Add: AddIcon,
    Search: SearchIcon,
} = MuiIcons;

const connector = connect(() => ({}), { setAlert });
type PropsFromRedux = ConnectedProps<typeof connector>;

/**
 * Sort order inside an expanded line: whatever needs looking at comes first.
 *
 * Unverified leads because it's the only state where we know nothing at all - it isn't "fine",
 * it's unmeasured. Then not found, then wrong type (found but wrong), and correct last, since a
 * correct line needs no action and is only there for completeness.
 */
function attentionRank(p: ILineSpecPublisher): number {
    if (p.isRemoved) return 4;
    if (p.status === null || p.status === undefined) return 0;
    if (p.status === 'missing') return 1;
    if (p.status === 'incorrect') return 2;
    return 3;
}

/**
 * The lines half of Publisher Lines: every distinct line across the seat, grouped by the line
 * itself rather than by publisher.
 *
 * Lines are a sibling of publishers, not a child, so this is a peer view rather than a
 * drill-down. That inversion is what makes "Add to publishers" possible: you look at one line,
 * see how many publishers carry it, and push it out to the ones that don't.
 */
const LinesTab: React.FC<PropsFromRedux> = ({ setAlert }) => {
    const queryClient = useQueryClient();

    const [filterModel, setFilterModel] = useState(() => getSavedFilterModel(tableNames.ALL_LINES));
    const [activating, setActivating] = useState<IDistinctLineSpec | null>(null);
    const [selectedPublishers, setSelectedPublishers] = useState<Set<string>>(new Set());
    // Removal keeps its own selection: the two dialogs offer different publishers (those
    // without the line vs those with it), so sharing one set would carry stale ticks across.
    const [removingFrom, setRemovingFrom] = useState<IDistinctLineSpec | null>(null);
    const [selectedForRemoval, setSelectedForRemoval] = useState<Set<string>>(new Set());
    const [importAllOpen, setImportAllOpen] = useState(false);
    const [generateOpen, setGenerateOpen] = useState(false);
    const [subTab, setSubTab] = useState(0);
    const [search, setSearch] = useState('');
    const [selectedOldIds, setSelectedOldIds] = useState<string[]>([]);
    const [deleteSelection, setDeleteSelection] = useState<IOldLineRow[] | null>(null);
    const lineWrapping = useWrappingGrid();
    // Old lines span cells across rows, which needs every row the same height - headers only.
    const oldLineWrapping = useWrappingGrid({ cells: false });

    const { data: specs, isLoading } = useQuery('distinctLineSpecs', fetchDistinctLineSpecs, {
        refetchOnWindowFocus: false,
    });

    const { data: publishers } = useQuery('trackedPublishers', fetchPublishersWithCounts, {
        refetchOnWindowFocus: false,
    });

    const { data: oldLines, isLoading: loadingOld } = useQuery('oldLines', fetchOldLines, {
        enabled: subTab === 1,
        refetchOnWindowFocus: false,
    });

    const handleFilterChange = (newFilterModel) => {
        setFilterModel(newFilterModel || { items: [] });
        saveFilterModel(tableNames.ALL_LINES, newFilterModel);
    };

    const selectedOldLines = (oldLines || []).filter(line => selectedOldIds.includes(line._id));
    const deletingPublisherCount = new Set((deleteSelection || []).map(line => line.publisherId)).size;
    const purgeMutation = useMutation(async (selection: IOldLineRow[]) => {
        // Reuse the publisher-scoped endpoint: it checks ownership and deletes only the
        // supplied old-line IDs. A retry after partial completion is safe.
        const byPublisher = new Map<string, string[]>();
        selection.forEach(line => byPublisher.set(line.publisherId, [...(byPublisher.get(line.publisherId) || []), line._id]));
        let purged = 0;
        for (const [publisherId, lineIds] of Array.from(byPublisher)) {
            purged += (await purgeRemovedLines(publisherId, lineIds)).purged;
        }
        return purged;
    }, {
        onSuccess: purged => {
            setAlert({ msg: `Deleted ${purged} old line(s) permanently.`, type: AlertTypes.SUCCESS });
            setSelectedOldIds([]);
            setDeleteSelection(null);
        },
        onSettled: () => {
            ['oldLines', 'distinctLineSpecs', 'trackedPublishers', 'trackedPublisher', 'trackedLines']
                .forEach(key => queryClient.invalidateQueries(key));
        },
    });

    const activateMutation = useMutation(
        ({ spec, publisherIds }: { spec: IDistinctLineSpec; publisherIds: string[] }) =>
            activateLineOnPublishers(
                {
                    domain: spec._id.domain,
                    pubId: spec._id.pubId,
                    relationship: spec._id.relationship,
                    certId: spec.certId,
                },
                publisherIds,
            ),
        {
            onSuccess: (result) => {
                const parts = [];
                if (result.restored) parts.push(`${result.restored} restored from removal requests`);
                if (result.added) parts.push(`${result.added} added fresh`);
                if (result.skipped) parts.push(`${result.skipped} already active`);
                setAlert({
                    msg: parts.length ? parts.join(', ') : 'Nothing to do — all selected publishers already had it.',
                    type: AlertTypes.SUCCESS,
                });
                setActivating(null);
                setSelectedPublishers(new Set());
                queryClient.invalidateQueries('distinctLineSpecs');
                queryClient.invalidateQueries('trackedPublishers');
            },
            onError: (err: any) => setAlert({ msg: errorMessage(err), type: AlertTypes.FAILURE }),
        },
    );

    const removeMutation = useMutation(
        ({ spec, publisherIds }: { spec: IDistinctLineSpec; publisherIds: string[] }) =>
            removeLineFromPublishers(
                {
                    domain: spec._id.domain,
                    pubId: spec._id.pubId,
                    relationship: spec._id.relationship,
                },
                publisherIds,
            ),
        {
            onSuccess: (result) => {
                setAlert({
                    msg: result.removed
                        ? `Moved ${result.removed} line(s) to Old lines.`
                        : 'Nothing to do — none of those publishers had this line active.',
                    type: AlertTypes.SUCCESS,
                });
                setRemovingFrom(null);
                setSelectedForRemoval(new Set());
                queryClient.invalidateQueries('distinctLineSpecs');
                queryClient.invalidateQueries('oldLines');
                queryClient.invalidateQueries('trackedPublishers');
            },
            onError: (err: any) => setAlert({ msg: errorMessage(err), type: AlertTypes.FAILURE }),
        },
    );

    /**
     * Total old lines, summed from the specs already on screen.
     *
     * Avoids a second request just to put a number on the tab - each spec already reports how
     * many of its publisher rows are old, and the sum is exactly what the Old lines grid lists.
     */
    const oldLineCount = useMemo(
        () => (specs || []).reduce((n, spec) => n + (spec.removedCount || 0), 0),
        [specs],
    );

    const rows = useMemo(
        () =>
            (specs || []).map((s) => ({
                ...s,
                id: `${s._id.domain}|${s._id.pubId}|${s._id.relationship}`,
                domain: s._id.domain,
                pubId: s._id.pubId,
                relationship: s._id.relationship,
                publisherCount: (s.publisherIds || []).length,
            })),
        [specs],
    );

    const columns: GridColDef[] = [
        { field: 'domain', headerName: 'Ad system domain', width: 220 },
        {
            field: 'pubId',
            headerName: 'Pub ID',
            width: 180,
            // "Publisher ID" read as though it identified the tracked publisher. It doesn't:
            // it's the account ID with the ad system in the first column, which is identical
            // for every publisher carrying this line - and is precisely why lines group by it.
            renderHeader: () => (
                <Tooltip title="The account ID with that ad system. It's the same for every publisher carrying this line, which is why lines group by it — it does not identify the publisher.">
                    <span style={{ fontWeight: 500 }}>Pub ID</span>
                </Tooltip>
            ),
        },
        { field: 'relationship', headerName: 'Relationship', width: 130 },
        { field: 'certId', headerName: 'Cert ID', width: 150 },
        {
            field: 'demands',
            headerName: 'Demand',
            width: 190,
            sortable: false,
            // Same trio the Publisher column below uses: 'flex' makes the cell a flex
            // container so align can work on it, and the header is centred to match.
            display: 'flex',
            align: 'center',
            headerAlign: 'center',
            /**
             * One row here is one line across every publisher carrying it, and they can each
             * annotate it differently ("Sovrn Adte" on one, "Sovrn Widesound" on another). All
             * of them are shown rather than picking a winner; the expanded row says which
             * publisher said what.
             */
            renderCell: (params: any) => {
                const demands: string[] = params.row.demands || [];
                if (!demands.length) return '';
                return (
                    <Tooltip title={demands.join(', ')}>
                        <Box
                            display="flex"
                            gap={0.5}
                            alignItems="center"
                            justifyContent="center"
                            flexWrap="nowrap"
                            sx={{ height: '100%' }}
                        >
                            {demands.slice(0, 2).map((d) => (
                                <Chip key={d} size="small" variant="outlined" label={d} />
                            ))}
                            {demands.length > 2 ? (
                                <Typography variant="caption" color="text.secondary">
                                    +{demands.length - 2}
                                </Typography>
                            ) : null}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'activeCount',
            headerName: 'Active on',
            width: 120,
            renderCell: (params: any) => `${params.value} pub.`,
        },
        {
            field: 'correct',
            headerName: 'Correct',
            width: 100,
            renderCell: (params: any) =>
                params.value ? <Chip size="small" color="success" variant="outlined" label={params.value} /> : '0',
        },
        {
            field: 'incorrect',
            headerName: 'Wrong type',
            width: 120,
            renderCell: (params: any) =>
                params.value ? <Chip size="small" color="warning" variant="outlined" label={params.value} /> : '0',
        },
        {
            field: 'missing',
            headerName: 'Not found',
            width: 110,
            renderCell: (params: any) =>
                params.value ? <Chip size="small" color="error" variant="outlined" label={params.value} /> : '0',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            width: 330,
            renderCell: (params: any) => (
                <Box display="flex" gap={0.5}>
                    <Tooltip title="Add this line to publishers that don't have it yet">
                        <Button
                            size="small"
                            startIcon={<PlaylistAddIcon />}
                            onClick={() => {
                                setActivating(params.row);
                                setSelectedPublishers(new Set());
                            }}
                        >
                            Add to
                        </Button>
                    </Tooltip>
                    <Tooltip title="Remove this line from some or all of the publishers carrying it">
                        <Button
                            size="small"
                            color="error"
                            startIcon={<PlaylistRemoveIcon />}
                            disabled={!params.row.activeCount}
                            onClick={() => {
                                setRemovingFrom(params.row);
                                setSelectedForRemoval(new Set());
                            }}
                        >
                            Remove from
                        </Button>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    /** The working set. Parked publishers stay on the list screen, but aren't pushed lines. */
    const activePublishers = useMemo(
        () => ((publishers as ITrackedPublisher[]) || []).filter((p) => p.isActive !== false),
        [publishers],
    );

    /**
     * Publishers that don't already have this line active - the only ones worth offering.
     * Those with it sitting in removal requests are pre-ticked, since re-activating restores
     * the original line and its history rather than creating a duplicate.
     */
    const eligible = useMemo(() => {
        if (!activating || !publishers) return [];
        const have = new Set(activating.publisherIds.map(String));
        return (publishers as ITrackedPublisher[])
            // Parked publishers aren't offered: pushing a line to one would be work nobody is
            // going to follow up on. Switch it back on first if that's what you meant.
            .filter((p) => p.isActive !== false)
            .map((p) => ({ ...p, alreadyTracked: have.has(String(p._id)) }))
            // Publishers that can actually be actioned first, alphabetically; the ones that
            // already carry the line sink to the bottom. With 50 publishers, a purely
            // alphabetical list buries the handful you came here to tick.
            .sort((a, b) => {
                if (a.alreadyTracked !== b.alreadyTracked) return a.alreadyTracked ? 1 : -1;
                return (a.name || '').localeCompare(b.name || '');
            });
    }, [activating, publishers]);

    const toggle = (id: string) =>
        setSelectedPublishers((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    /** Publishers that can actually receive this line - the ones already carrying it are inert. */
    const selectable = useMemo(() => eligible.filter((p) => !p.alreadyTracked), [eligible]);
    const allSelectableChosen =
        selectable.length > 0 && selectable.every((p) => selectedPublishers.has(p._id));

    const toggleAllSelectable = () =>
        setSelectedPublishers((prev) =>
            allSelectableChosen && prev.size ? new Set() : new Set(selectable.map((p) => p._id)),
        );


    /**
     * All-lines search. Reaches the publisher names a line is carried by, which is the most
     * useful thing to search here and isn't a column at all - the grid only shows a count.
     *
     * The count columns are excluded deliberately: they're small integers, so searching "2"
     * would match most of the table through a column nobody searches by.
     */
    const filteredRows = useMemo(
        () =>
            searchRows(rows, search, (r: any) => [
                r.domain,
                r.pubId,
                r.relationship,
                r.certId,
                (r.demands || []).join(' '),
                ...(r.publishers || []).flatMap((p: ILineSpecPublisher) => [p.name, p.publisherDomain]),
            ]),
        [rows, search],
    );

    /** Old lines carry their publisher directly, so the haystack is flatter. */
    const filteredOldLines = useMemo(
        () =>
            searchRows(oldLines || [], search, (r: any) => [
                r.domain,
                r.pubId,
                r.relationship,
                r.certId,
                r.demand,
                r.publisherName,
                r.publisherDomain,
                // The same words the "Still live?" column shows.
                r.needsRecheck
                    ? 'not checked since removal'
                    : r.foundInFile === true
                    ? 'still live'
                    : r.foundInFile === false
                    ? 'confirmed removed'
                    : 'unknown',
                r.removedAt ? dateToStringFormat(r.removedAt) : '',
                r.lastCheckedAt ? dateToStringFormat(r.lastCheckedAt) : 'never',
            ]),
        [oldLines, search],
    );

    /**
     * Publishers this line is currently active on - the only ones there is anything to remove
     * from. A publisher that already has it in Old lines is listed but inert, the same way the
     * add dialog shows who already has the line.
     */
    const removalCandidates = useMemo(() => {
        if (!removingFrom) return [];
        return (removingFrom.publishers || [])
            .map((p) => ({ ...p, alreadyRemoved: !!p.isRemoved }))
            .sort((a, b) => {
                if (a.alreadyRemoved !== b.alreadyRemoved) return a.alreadyRemoved ? 1 : -1;
                return (a.name || '').localeCompare(b.name || '');
            });
    }, [removingFrom]);

    const removable = useMemo(() => removalCandidates.filter((p) => !p.alreadyRemoved), [removalCandidates]);
    const allRemovableChosen =
        removable.length > 0 && removable.every((p) => selectedForRemoval.has(p.publisherId));

    const toggleRemoval = (id: string) =>
        setSelectedForRemoval((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const toggleAllRemovable = () =>
        setSelectedForRemoval((prev) =>
            allRemovableChosen && prev.size ? new Set() : new Set(removable.map((p) => p.publisherId)),
        );

    /**
     * The expanded row: which publishers carry this line, and in what state.
     *
     * The counts on the row tell you there's something to look at; this tells you where. Old
     * lines appear here too, which is why the separate "Removal req." count column went away -
     * it said a number without saying whose.
     */
    const detailPanel = React.useCallback(
        ({ row }: any) => {
            const publishers: ILineSpecPublisher[] = row.publishers || [];
            const active = publishers.filter((p) => !p.isRemoved);
            const old = publishers.filter((p) => p.isRemoved);

            const statusChip = (p: ILineSpecPublisher) => {
                if (p.isRemoved) {
                    return (
                        <Chip
                            size="small"
                            variant="outlined"
                            color={p.foundInFile === true ? 'error' : p.foundInFile === false ? 'success' : 'info'}
                            label={
                                p.foundInFile === true
                                    ? 'old — still live'
                                    : p.foundInFile === false
                                    ? 'old — gone'
                                    : 'old — unchecked'
                            }
                        />
                    );
                }
                if (p.status === 'correct') return <Chip size="small" color="success" variant="outlined" label="live & correct" />;
                if (p.status === 'incorrect') return <Chip size="small" color="warning" variant="outlined" label="wrong type" />;
                if (p.status === 'missing') return <Chip size="small" color="error" variant="outlined" label="not found" />;
                return <Chip size="small" variant="outlined" label="unverified" />;
            };

            /**
             * Two centred lines: name plus its status, then the domain and when it was checked.
             *
             * Centring is set explicitly rather than left to inherit - a global `text-align:
             * center` in App.css was reaching some of these and not others, which is why the
             * name looked centred while the line under it sat hard left.
             */
            const entry = (p: ILineSpecPublisher) => (
                <Box
                    key={p.publisherId}
                    sx={{
                        textAlign: 'center',
                        py: 1.25,
                        px: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <Typography variant="body2" noWrap>
                            {p.name || '(deleted publisher)'}
                        </Typography>
                        {statusChip(p)}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {p.publisherDomain}
                        {p.lastCheckedAt ? ` · checked ${dateToStringFormat(p.lastCheckedAt)}` : ' · never checked'}
                    </Typography>
                    {/* The grid row lists every distinct demand; this is where you find out
                        which publisher is responsible for which of them. */}
                    {!!p.demand && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            demand: {p.demand}
                        </Typography>
                    )}
                </Box>
            );

            /**
             * Flowed into as many columns as fit rather than one long list.
             *
             * A line on 58 publishers made the expanded row taller than the screen, so the grid
             * underneath vanished. auto-fill keeps it a few rows deep at any window width.
             */
            const group = (title: string, rows: ILineSpecPublisher[]) =>
                !!rows.length && (
                    <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom textAlign="center">
                            {title}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                // Wider minimum and a real gutter: 300px gave five cramped
                                // columns on a wide screen, which read as a wall of text.
                                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                                columnGap: 6,
                                rowGap: 0.5,
                            }}
                        >
                            {rows
                                .slice()
                                .sort(
                                    (a, b) =>
                                        attentionRank(a) - attentionRank(b) ||
                                        (a.name || '').localeCompare(b.name || ''),
                                )
                                .map(entry)}
                        </Box>
                    </Box>
                );

            return (
                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        gutterBottom
                        textAlign="center"
                        sx={{ fontFamily: 'monospace' }}
                    >
                        {row.domain}, {row.pubId}, {row.relationship}
                        {row.certId ? `, ${row.certId}` : ''}
                    </Typography>
                    {group(`Active on ${active.length} publisher(s)`, active)}
                    {group(`Old on ${old.length} publisher(s)`, old)}
                    {!publishers.length && (
                        <Typography variant="body2" color="text.secondary">
                            No publishers carry this line.
                        </Typography>
                    )}
                </Box>
            );
        },
        [],
    );

    /**
     * Row spanning applies to every column by default, merging any repeated consecutive value.
     * That collapsed Relationship, "Still live?" and Last checked too, because two old lines for
     * the same publisher legitimately share those - so the second row rendered blank.
     *
     * Giving a column a span key that's unique per row switches spanning off for it. Only the
     * publisher column is allowed to merge.
     */
    const neverSpan = { rowSpanValueGetter: (_value: any, row: any) => row?._id };

    const oldLineColumns: GridColDef[] = [
        // Keep a separate checkbox for each line even when publisher cells are merged.
        { ...MuiDataGrid.GRID_CHECKBOX_SELECTION_COL_DEF, ...neverSpan },
        {
            field: 'publisherName',
            headerName: 'Publisher',
            width: 200,
            /**
             * Spans this publisher's rows into one merged cell, so a publisher with three old
             * lines reads as one group rather than its name repeated three times.
             *
             * Keyed on publisherId rather than the displayed name: two publishers could share a
             * name, and merging those would be wrong. Requires rows to be adjacent, which the
             * server guarantees by sorting on publisher name.
             */
            rowSpanValueGetter: (_value, row) => row?.publisherId,
            // 'flex' makes the cell a flex container, which is what allows the name to sit in
            // the vertical middle of the rows it spans rather than at the top of the first one.
            display: 'flex',
            align: 'center',
            headerAlign: 'center',
        },
        { field: 'domain', headerName: 'Ad system domain', width: 200, ...neverSpan },
        { field: 'pubId', headerName: 'Pub ID', width: 170, ...neverSpan },
        { field: 'relationship', headerName: 'Relationship', width: 120, ...neverSpan },
        // neverSpan matters here: two old lines for one publisher often share a demand, and
        // without it the second row would render blank rather than repeat the value.
        { field: 'demand', headerName: 'Demand', width: 150, ...neverSpan },
        {
            field: 'foundInFile',
            headerName: 'Still live?',
            width: 220,
            ...neverSpan,
            valueGetter: (params: any) =>
                params.row.needsRecheck
                    ? 'not checked since removal'
                    : params.row.foundInFile === true
                    ? 'still live'
                    : params.row.foundInFile === false
                    ? 'confirmed removed'
                    : 'unknown',
            renderCell: (params: any) => (
                <Chip
                    size="small"
                    variant="outlined"
                    color={
                        params.row.needsRecheck
                            ? 'info'
                            : params.row.foundInFile === true
                            ? 'error'
                            : params.row.foundInFile === false
                            ? 'success'
                            : 'default'
                    }
                    label={params.value}
                />
            ),
        },
        {
            field: 'removedAt',
            headerName: 'Removed at',
            width: 180,
            ...neverSpan,
            valueGetter: (params: any) => (params.row.removedAt ? dateToStringFormat(params.row.removedAt) : '-'),
        },
        {
            field: 'lastCheckedAt',
            headerName: 'Last checked',
            width: 180,
            ...neverSpan,
            valueGetter: (params: any) =>
                params.row.lastCheckedAt ? dateToStringFormat(params.row.lastCheckedAt) : 'never',
        },
    ];

    return (
        <Box>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setImportAllOpen(true)}
                >
                    Add lines to publishers
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlaylistAddIcon />}
                    onClick={() => setGenerateOpen(true)}
                >
                    Generate lines
                </Button>
            </Box>

            {/* Old lines are a view of the same data, so a sub-tab rather than a filter the
                user has to remember to set. */}
            <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 1 }}>
                <Tab label={`All lines (${rows.length})`} />
                <Tab label={`Old lines (${oldLines ? oldLines.length : oldLineCount})`} />
            </Tabs>

            {/* Below the sub-tabs, so it reads as applying to the list underneath. One box for
                both: the search carries across when you switch, which is what you want when
                chasing one line between the two views. */}
            <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
                <TextField
                    size="small"
                    placeholder={subTab === 0 ? 'Search lines' : 'Search old lines'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 320 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                {subTab === 1 && (
                    <Button color="error" variant="outlined" size="small"
                        disabled={!selectedOldLines.length || purgeMutation.isLoading}
                        onClick={() => { purgeMutation.reset(); setDeleteSelection([...selectedOldLines]); }}>
                        Delete selected ({selectedOldLines.length})
                    </Button>
                )}
                {!!search.trim() && (
                    <Typography variant="body2" color="text.secondary">
                        {subTab === 0
                            ? `${filteredRows.length} of ${rows.length} shown`
                            : `${filteredOldLines.length} of ${(oldLines || []).length} shown`}
                    </Typography>
                )}
            </Box>

            {subTab === 0 ? (
                <DataGridPro
                    {...lineWrapping.gridProps}
                    disableColumnPinning
                    rows={filteredRows}
                    columns={columns}
                    loading={isLoading}
                    autoHeight
                    getRowId={(row) => row.id}
                    pageSize={100}
                    rowsPerPageOptions={[25, 50, 100]}
                    disableSelectionOnClick
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterChange}
                    // Expandable rows: the counts say there's something to look at, the panel
                    // says which publishers.
                    getDetailPanelContent={detailPanel}
                    getDetailPanelHeight={() => 'auto' as any}
                    style={{ minHeight: '55vh' }}
                />
            ) : null}

            {subTab === 0 && !isLoading && !!rows.length && !filteredRows.length && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        No line matches "{search}".
                    </Typography>
                </Box>
            )}

            {subTab === 1 ? (
                <>
                    <Alert severity="info" sx={{ mb: 1 }}>
                        Lines you've asked publishers to remove, across every publisher. They stay here until a check
                        confirms they're gone, or until you select and permanently delete them here or in the publisher's Old lines tab.
                    </Alert>
                    <DataGridPro
                        {...oldLineWrapping.gridProps}
                        disableColumnPinning
                        rows={filteredOldLines}
                        columns={oldLineColumns}
                        loading={loadingOld}
                        autoHeight
                        getRowId={(row) => row._id}
                        pageSize={100}
                        rowsPerPageOptions={[25, 50, 100]}
                        pagination
                        checkboxSelection
                        checkboxSelectionVisibleOnly
                        disableRowSelectionExcludeModel
                        disableRowSelectionOnClick
                        rowSelectionModel={selectedOldIds}
                        onRowSelectionModelChange={ids => {
                            if (!purgeMutation.isLoading && !deleteSelection) setSelectedOldIds(ids.map(String));
                        }}
                        rowSpanning
                        style={{ minHeight: '55vh' }}
                    />
                    {!loadingOld && !(oldLines || []).length && (
                        <Box mt={2}>
                            <Typography variant="body2" color="text.secondary">
                                No old lines. Removing a line from a publisher puts it here until a check confirms the
                                publisher has taken it out.
                            </Typography>
                        </Box>
                    )}
                    {!loadingOld && !!(oldLines || []).length && !filteredOldLines.length && (
                        <Box mt={2}>
                            <Typography variant="body2" color="text.secondary">
                                No old line matches "{search}".
                            </Typography>
                        </Box>
                    )}
                </>
            ) : null}

            <Dialog open={!!deleteSelection} onClose={() => {
                if (!purgeMutation.isLoading) setDeleteSelection(null);
            }} fullWidth maxWidth="sm">
                <DialogTitle>Delete selected old lines?</DialogTitle>
                <DialogContent>
                    Permanently delete {deleteSelection?.length ?? 0} old line(s) across {deletingPublisherCount} publisher(s),
                    including their change history? This cannot be undone.
                    {!!purgeMutation.error && <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage(purgeMutation.error)} Some deletions may already have completed. You can retry this selection.
                    </Alert>}
                </DialogContent>
                <DialogActions>
                    <Button autoFocus disabled={purgeMutation.isLoading} onClick={() => setDeleteSelection(null)}>Cancel</Button>
                    <Button color="error" disabled={purgeMutation.isLoading || !deleteSelection?.length}
                        onClick={() => { if (deleteSelection?.length && !purgeMutation.isLoading) purgeMutation.mutate(deleteSelection); }}>
                        {purgeMutation.isLoading ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ImportToAllModal
                open={importAllOpen}
                // Parked publishers aren't offered here either - the same reasoning as the
                // "Add to" dialog above.
                publishers={activePublishers}
                onClose={() => setImportAllOpen(false)}
                onDone={(summary) => {
                    setAlert({ msg: summary, type: AlertTypes.SUCCESS });
                    queryClient.invalidateQueries('distinctLineSpecs');
                    queryClient.invalidateQueries('trackedPublishers');
                }}
            />

            <GenerateLinesModal
                open={generateOpen}
                onClose={() => setGenerateOpen(false)}
                onSaved={(result) => {
                    setAlert({ msg: describeSaveResult(result), type: AlertTypes.SUCCESS });
                    // The saved lines are new rows in this very grid, so it has to reload.
                    queryClient.invalidateQueries('distinctLineSpecs');
                    queryClient.invalidateQueries('trackedPublishers');
                }}
            />

            <Dialog open={!!activating} onClose={() => setActivating(null)} fullWidth maxWidth="sm">
                <DialogTitle>Add line to publishers</DialogTitle>
                <DialogContent dividers>
                    {activating && (
                        <>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }} gutterBottom>
                                {activating._id.domain}, {activating._id.pubId}, {activating._id.relationship}
                                {activating.certId ? `, ${activating.certId}` : ''}
                            </Typography>
                            {!!activating.demands?.length && (
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Demand: {activating.demands.join(', ')}
                                </Typography>
                            )}

                            <Alert severity="info" sx={{ my: 2 }}>
                                Where a publisher previously had this exact line and it was removed, ticking it here
                                restores that line with its original history rather than adding a duplicate.
                            </Alert>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={allSelectableChosen}
                                        // Only the publishers that can receive the line: ticking the
                                        // ones that already have it would be a no-op the server
                                        // would just skip.
                                        onChange={toggleAllSelectable}
                                        disabled={!selectable.length}
                                    />
                                }
                                label={`Select all (${selectable.length} without this line)`}
                            />

                            <List dense>
                                {eligible.map((p) => (
                                    <ListItem key={p._id} disableGutters>
                                        <FormControlLabel
                                            // Disabled rather than hidden: seeing who already has
                                            // the line is useful, ticking them is not.
                                            disabled={p.alreadyTracked}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    disabled={p.alreadyTracked}
                                                    checked={selectedPublishers.has(p._id)}
                                                    onChange={() => toggle(p._id)}
                                                />
                                            }
                                            label={
                                                <span>
                                                    {p.name}{' '}
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        ({p.domain})
                                                        {p.alreadyTracked ? ' — already has this line' : ''}
                                                    </Typography>
                                                </span>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            {!eligible.length && (
                                <Typography variant="body2" color="text.secondary">
                                    No publishers available.
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
                        {selectedPublishers.size} selected
                    </Typography>
                    <Button onClick={() => setActivating(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!selectedPublishers.size || activateMutation.isLoading}
                        onClick={() =>
                            activateMutation.mutate({ spec: activating, publisherIds: Array.from(selectedPublishers) })
                        }
                    >
                        Add line
                    </Button>
                </DialogActions>
            </Dialog>

            {/* The mirror of the dialog above: same shape, opposite direction. Its list comes
                from the row itself rather than every publisher in the seat, because the only
                publishers you can remove a line from are the ones carrying it. */}
            <Dialog open={!!removingFrom} onClose={() => setRemovingFrom(null)} fullWidth maxWidth="sm">
                <DialogTitle>Remove line from publishers</DialogTitle>
                <DialogContent dividers>
                    {removingFrom && (
                        <>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }} gutterBottom>
                                {removingFrom._id.domain}, {removingFrom._id.pubId}, {removingFrom._id.relationship}
                                {removingFrom.certId ? `, ${removingFrom.certId}` : ''}
                            </Typography>
                            {!!removingFrom.demands?.length && (
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Demand: {removingFrom.demands.join(', ')}
                                </Typography>
                            )}

                            <Alert severity="info" sx={{ my: 2 }}>
                                Removing moves the line to Old lines for each publisher picked. It isn't deleted —
                                checks keep telling you whether the publisher has actually taken it out, and you can
                                put it back with its history intact.
                            </Alert>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={allRemovableChosen}
                                        onChange={toggleAllRemovable}
                                        disabled={!removable.length}
                                    />
                                }
                                label={`Select all (${removable.length} carrying this line)`}
                            />

                            <List dense>
                                {removalCandidates.map((p) => (
                                    <ListItem key={p.publisherId} disableGutters>
                                        <FormControlLabel
                                            disabled={p.alreadyRemoved}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    disabled={p.alreadyRemoved}
                                                    checked={selectedForRemoval.has(p.publisherId)}
                                                    onChange={() => toggleRemoval(p.publisherId)}
                                                />
                                            }
                                            label={
                                                <span>
                                                    {p.name}{' '}
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        ({p.publisherDomain})
                                                        {p.alreadyRemoved ? ' — already in Old lines' : ''}
                                                    </Typography>
                                                </span>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            {!removalCandidates.length && (
                                <Typography variant="body2" color="text.secondary">
                                    No publishers carry this line.
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
                        {selectedForRemoval.size} selected
                    </Typography>
                    <Button onClick={() => setRemovingFrom(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!selectedForRemoval.size || removeMutation.isLoading}
                        onClick={() =>
                            removeMutation.mutate({
                                spec: removingFrom,
                                publisherIds: Array.from(selectedForRemoval),
                            })
                        }
                    >
                        Remove line
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default connector(LinesTab);
